import React, { useState, useCallback, useMemo } from 'react';
import { useApiMode } from '../context/ApiModeContext';
import './ApiExplorer.css';

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const buildFullUrl = (base, endpoint, pathParamValues = {}) => {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const filled = path.replace(/\{(\w+)\}/g, (_, key) => pathParamValues[key] ?? '');
  return `${base.replace(/\/$/, '')}${filled}`;
};

const SimpleApiExplorer = ({
  apiName,
  endpoint: singleEndpoint,
  method: singleMethod = 'POST',
  sampleRequest: singleSampleRequest = {},
  sampleResponse: singleSampleResponse = {},
  tabs: tabsConfig,
  baseUrl: baseUrlProp,
  onClose,
}) => {
  const { apiBaseUrl: contextBaseUrl } = useApiMode();
  const baseUrl = baseUrlProp || contextBaseUrl;
  const base = baseUrl.replace(/\/$/, '');
  const hasTabs = Array.isArray(tabsConfig) && tabsConfig.length > 0;

  const tabs = hasTabs
    ? tabsConfig
    : [
        {
          id: 'default',
          label: 'Request',
          method: singleMethod,
          endpoint: singleEndpoint,
          sampleRequest: singleSampleRequest,
          sampleResponse: singleSampleResponse,
        },
      ];

  const [activeTabId, setActiveTabId] = useState(tabs[0].id);
  const [pathParamValues, setPathParamValues] = useState(() => {
    const initial = {};
    tabs.forEach((t) => {
      (t.pathParams || []).forEach((p) => {
        if (p.name && initial[p.name] === undefined) initial[p.name] = p.placeholder ?? '';
      });
    });
    return initial;
  });
  const [responseText, setResponseText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const activeTab = useMemo(() => tabs.find((t) => t.id === activeTabId) || tabs[0], [tabs, activeTabId]);
  const fullUrl = useMemo(
    () => buildFullUrl(base, activeTab.endpoint, pathParamValues),
    [base, activeTab.endpoint, pathParamValues]
  );
  const hasRequestBody = activeTab.method !== 'GET' && activeTab.sampleRequest != null;
  const requestStr =
    typeof activeTab.sampleRequest === 'string'
      ? activeTab.sampleRequest
      : JSON.stringify(activeTab.sampleRequest ?? {}, null, 2);
  const responseStr =
    typeof activeTab.sampleResponse === 'string'
      ? activeTab.sampleResponse
      : JSON.stringify(activeTab.sampleResponse ?? {}, null, 2);

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  const handleSend = async () => {
    setError('');
    setIsLoading(true);
    try {
      const method = activeTab.method || 'GET';
      const opts = method === 'GET' ? {} : { method, headers: { 'Content-Type': 'application/json' }, body: hasRequestBody ? (typeof activeTab.sampleRequest === 'string' ? activeTab.sampleRequest : JSON.stringify(activeTab.sampleRequest ?? {})) : undefined };
      const r = await fetch(fullUrl, opts);
      const res = await r.json().catch(() => ({}));
      setResponseText(JSON.stringify(res, null, 2));
    } catch (e) {
      setError(e.message || 'Request failed');
      setResponseText('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setResponseText('');
    setError('');
  };

  const setPathParam = (name, value) => {
    setPathParamValues((prev) => ({ ...prev, [name]: value }));
  };

  const defaultErrors = [
    { code: '400', description: 'Bad Request – Invalid or missing required fields' },
    { code: '401', description: 'Unauthorized – Invalid or missing API key' },
    { code: '404', description: 'Not Found – Resource not found' },
    { code: '422', description: 'Unprocessable Entity – Validation failed' },
    { code: '500', description: 'Internal Server Error' },
  ];

  const pathParams = activeTab.pathParams || [];

  return (
    <div className="api-explorer api-explorer-sidebar">
      <div className="api-explorer-header">
        <h3>{apiName}</h3>
        <button type="button" className="api-explorer-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      {hasTabs && (
        <div className="api-explorer-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`api-tab ${activeTabId === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTabId(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="api-explorer-panel">
        <div className="api-endpoint-block">
          <label className="api-endpoint-label">Base API endpoint</label>
          <div className="api-endpoint-value">{baseUrl}</div>
          <label className="api-endpoint-label">API endpoint</label>
          <div className="api-endpoint-row">
            <input type="text" className="api-endpoint-input" value={fullUrl} readOnly />
            <button
              type="button"
              className="api-endpoint-copy"
              onClick={() => copyToClipboard(fullUrl)}
              title="Copy endpoint"
              aria-label="Copy endpoint"
            >
              <CopyIcon />
            </button>
          </div>
          <div className="api-method-badge-inline">
            <span className="api-method-badge">{activeTab.method || 'GET'}</span>
          </div>
        </div>

        {pathParams.length > 0 && (
          <section className="api-doc-section">
            <h4>Path parameters</h4>
            <div className="api-form">
              {pathParams.map((p) => (
                <div key={p.name} className="form-group">
                  <label>{p.name}</label>
                  <input
                    type="text"
                    className="form-input"
                    value={pathParamValues[p.name] ?? ''}
                    onChange={(e) => setPathParam(p.name, e.target.value)}
                    placeholder={p.placeholder ?? ''}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="api-doc-section">
          <h4>{hasRequestBody ? 'Request body' : 'Request'}</h4>
          {hasRequestBody ? (
            <pre className="api-pre api-response-pre">{requestStr}</pre>
          ) : (
            <pre className="api-pre">No request body. {activeTab.method || 'GET'} request.</pre>
          )}
          <div className="api-tab-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSend} disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          </div>
        </section>

        <section className="api-doc-section">
          <h4>Response</h4>
          <pre className="api-pre api-response-pre">{responseStr}</pre>
        </section>

        <section className="api-doc-section">
          <h4>Error</h4>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {(activeTab.errors || defaultErrors).map((row) => (
                <tr key={row.code}>
                  <td><code>{row.code}</code></td>
                  <td>{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <div className="api-response-section">
        <h4>Live response</h4>
        {error && <div className="error-message">{error}</div>}
        <textarea
          className="api-textarea response-textarea"
          readOnly
          value={responseText || (isLoading ? 'Loading...' : 'Click Send to see response')}
          rows={6}
        />
      </div>
    </div>
  );
};

export default SimpleApiExplorer;
